import { Component, DestroyRef, Injector, Input, OnInit, Signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { SmartCartService } from '@pages/engage/smart-cart/smart-cart.service';
import {
  UploadIconDialogComponent,
  UploadIconDialogResult,
} from '@app/sharedModule/components/upload-icon-dialog/upload-icon-dialog.component';
import { GlobalSettingsFormShape } from '../../../../../components/smart-cart-form-global-settings/global-settings.model';
import { GOAL_PRESET_ICON_URLS, GoalFormShape, GoalsBarType, PillSource } from '../../../goals-progress-bar.model';

// Display controls mirror the widget's nullable design controls: they always show a value (widget
// override or global fallback), while the `*Overridden` signals track whether an override is set.
interface LinkedButtonDesign {
  displayBackgroundColor: FormControl<string>;
  displayTextColor: FormControl<string>;
  displayHoverColor: FormControl<string>;
  displayCornerRadius: FormControl<number>;
  backgroundColorOverridden: Signal<boolean>;
  textColorOverridden: Signal<boolean>;
  hoverColorOverridden: Signal<boolean>;
  cornerRadiusOverridden: Signal<boolean>;
}

@Component({
  selector: 'smart-cart-goal-appearance-tab',
  templateUrl: './goal-appearance-tab.component.html',
  styleUrls: ['../../../../../components/shared.scss'],
  standalone: false,
})
export class SmartCartGoalAppearanceTabComponent implements OnInit {
  @Input({ required: true }) goal!: FormGroup<GoalFormShape>;
  @Input({ required: true }) barType!: FormControl<GoalsBarType>;
  @Input({ required: true }) pillsEnabled!: FormControl<boolean>;
  @Input({ required: true }) globalSettingsFormGroup!: FormGroup<GlobalSettingsFormShape>;
  @Input() smartCartId: number | null = null;
  @Input() usageToken = '';

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly smartCartService = inject(SmartCartService);
  private readonly injector = inject(Injector);

  readonly presetIconUrls = GOAL_PRESET_ICON_URLS;

  readonly labelPositionOptions: DropdownOption[] = [
    { label: 'Above bar', value: 'above_bar' },
    { label: 'Below bar', value: 'below_bar' },
  ];

  readonly paddingOptions: DropdownOption[] = [
    { label: 'None', value: 'none' },
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
  ];

  readonly imageFitOptions: DropdownOption[] = [
    { label: 'Square', value: 'square' },
    { label: 'Crop', value: 'crop' },
    { label: 'Fill', value: 'fill' },
  ];

  applySectionCollapsed = false;
  iconUploadError: string | null = null;
  uploadingIcon = false;

  // Initialized eagerly so the template always has defined controls/signals to bind to;
  // ngOnInit wires them to the widget/global controls.
  readonly applyButtonDesign: LinkedButtonDesign = this.buildEmptyLinkedDesign();

  ngOnInit(): void {
    this.wireApplyButtonDesign();
  }

  resetToGlobal(widgetCtrl: AbstractControl): void {
    widgetCtrl.setValue(null);
  }

  toggleApplySectionCollapsed(): void {
    this.applySectionCollapsed = !this.applySectionCollapsed;
  }

  onOrderDiscountMessageChange(html: string): void {
    this.goal.controls.appearance.controls.applySection.controls.texts.controls.orderDiscountMessage.setValue(html);
  }

  setPillSource(source: PillSource): void {
    this.goal.controls.appearance.controls.pillSource.setValue(source);
  }

  onIconUrlChange(value: string | File): void {
    this.goal.controls.appearance.controls.iconUrl.setValue(value);
  }

  onUploadCustomIcon(): void {
    const appearance = this.goal.controls.appearance;
    this.dialog
      .open<UploadIconDialogComponent, void, UploadIconDialogResult | null>(UploadIconDialogComponent, {
        width: '576px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        this.iconUploadError = null;
        if (this.smartCartId === null) {
          this.iconUploadError = 'Save the cart once before uploading an icon.';
          return;
        }
        this.uploadingIcon = true;
        this.smartCartService
          .uploadIcon(this.smartCartId, this.usageToken, 'goals-icons', result.file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: ({ url }) => {
              appearance.controls.customIconUrl.setValue(url);
              appearance.controls.iconUrl.setValue(url);
              this.uploadingIcon = false;
            },
            error: (err) => {
              console.error('[Smart Cart] goal icon upload failed', err);
              this.iconUploadError = 'Upload failed. Please try again.';
              this.uploadingIcon = false;
            },
          });
      });
  }

  onRemoveCustomIcon(): void {
    const appearance = this.goal.controls.appearance;
    const customValue = appearance.controls.customIconUrl.value;
    const wasSelected = !!customValue && appearance.controls.iconUrl.value === customValue;
    appearance.controls.customIconUrl.setValue('');
    if (wasSelected) {
      appearance.controls.iconUrl.setValue('');
    }
  }

  private buildEmptyLinkedDesign(): LinkedButtonDesign {
    const placeholder = computed(() => false);
    return {
      displayBackgroundColor: new FormControl<string>('', { nonNullable: true }),
      displayTextColor: new FormControl<string>('', { nonNullable: true }),
      displayHoverColor: new FormControl<string>('', { nonNullable: true }),
      displayCornerRadius: new FormControl<number>(0, { nonNullable: true }),
      backgroundColorOverridden: placeholder,
      textColorOverridden: placeholder,
      hoverColorOverridden: placeholder,
      cornerRadiusOverridden: placeholder,
    };
  }

  private wireApplyButtonDesign(): void {
    const widget = this.goal.controls.appearance.controls.applySection.controls.button.controls;
    const global = this.globalSettingsFormGroup.controls.buttonDesign.controls;
    const linked = this.applyButtonDesign;

    this.wireDisplayString(linked.displayBackgroundColor, widget.backgroundColor, global.backgroundColor);
    this.wireDisplayString(linked.displayTextColor, widget.textColor, global.textColor);
    this.wireDisplayString(linked.displayHoverColor, widget.hoverColor, global.hoverTextColor);
    this.wireDisplayNumber(linked.displayCornerRadius, widget.cornerRadius, global.cornerRadius);

    linked.backgroundColorOverridden = this.overriddenSignal(widget.backgroundColor);
    linked.textColorOverridden = this.overriddenSignal(widget.textColor);
    linked.hoverColorOverridden = this.overriddenSignal(widget.hoverColor);
    linked.cornerRadiusOverridden = this.overriddenSignal(widget.cornerRadius);
  }

  private wireDisplayString(
    display: FormControl<string>,
    widgetCtrl: FormControl<string | null>,
    globalCtrl: AbstractControl,
  ): void {
    const syncDown = (): void =>
      display.setValue(widgetCtrl.value ?? (globalCtrl.value as string), { emitEvent: false });
    syncDown();
    widgetCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    globalCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      // Empty input ↔ inherit (null), so clearing reverts to the global fallback.
      const value = next || null;
      if (widgetCtrl.value !== value) widgetCtrl.setValue(value);
    });
  }

  private wireDisplayNumber(
    display: FormControl<number>,
    widgetCtrl: FormControl<number | null>,
    globalCtrl: AbstractControl,
  ): void {
    const syncDown = (): void =>
      display.setValue(widgetCtrl.value ?? (globalCtrl.value as number), { emitEvent: false });
    syncDown();
    widgetCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    globalCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(syncDown);
    display.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((next) => {
      if (widgetCtrl.value !== next) widgetCtrl.setValue(next);
    });
  }

  private overriddenSignal(widgetCtrl: AbstractControl): Signal<boolean> {
    const value = toSignal(widgetCtrl.valueChanges, {
      initialValue: widgetCtrl.value,
      injector: this.injector,
    });
    return computed(() => value() !== null);
  }
}
